/* Az Bilinenler — vokal. Şan derslerinde ezberlenen tavsiyelerin ve süslemelerin arkasındaki nedenler.
   Blok türleri: ../lessons/blocks.ts. Sources sit right above each fact as English comments: one line per
   claim, at least two sources each. */
import { cell, compare, go, h, list, p, play, pn, steps, table, tip } from '../lessons/blocks';
import type { Fact } from './types';

/** A3 and B3 alternating in 1/8 beats, ending on A3: a trill on A3. */
const TRILL = `${'A3:0.125 B3:0.125 '.repeat(6)}A3:2`;

export const vokal: Fact[] = [
  // Sources:
  // - Melisma: several notes on one syllable, the opposite of syllabic singing: https://en.wikipedia.org/wiki/Melisma ;
  //   https://www.vocabulary.com/dictionary/melisma
  // - "Nağme" means melody: https://sozluk.gov.tr/gts?ara=nağme ; https://archives.ismir.net/ismir2011/paper/000062.pdf
  // - Hançere = gırtlak; "gırtlak yapmak / atmak / gırtlak nağmesi" name a folk singer's ornamenting skill, made
  //   of trills, mordents, vibrato, çarpma, appoggiaturas and turns produced at the vocal folds, graded soft to
  //   hard, also in fast dance songs: https://sozluk.gov.tr/gts?ara=hançere ;
  //   https://www.idildergisi.com/makale/pdf/1625623259.pdf (Uslu & Doğrusöz Dişiaçık 2021) ;
  //   https://dergipark.org.tr/tr/download/article-file/4571299 (Gündoğdu 2025)
  // - Uzun hava is sung in free rhythm and is the more ornamented side of folk singing:
  //   https://tr.wikipedia.org/wiki/Uzun_hava ; https://polen.itu.edu.tr/items/119aca35-2ba3-4a58-8d83-9bbfed097011 ;
  //   https://www.ktb.gov.tr/EN-120260/varieties-of-style-scale-and-rhythm-and-spheres-of-use.html ;
  //   https://folkloredebiyat.org/DergiPdfDetay.aspx?ID=1373 ; https://dergipark.org.tr/en/download/article-file/1355137
  // - Arabesk is emotional, ornamented, with a trembling voice, slides and microtonal shifts:
  //   https://tr.wikipedia.org/wiki/Arabesk_müzik ; https://dergipark.org.tr/en/download/article-file/2357097 ;
  //   https://dergipark.org.tr/en/download/article-file/4925970 ; https://dunyainc.org/wp-content/uploads/2011/07/Arabesk-04-program.pdf
  // - Müslüm Gürses, Ferdi Tayfur and Ümit Yaşar are arabesk singers: https://en.wikipedia.org/wiki/M%C3%BCsl%C3%BCm_G%C3%BCrses ;
  //   https://www.hurriyetdailynews.com/muslum-gurses-the-father-of-turkish-arabesque-152611 ;
  //   https://en.wikipedia.org/wiki/Ferdi_Tayfur ; https://www.dailysabah.com/arts/music/turkish-arabesque-music-icon-ferdi-tayfur-dies-at-79 ;
  //   https://www.haberler.com/0-yillik-ibo-sov-kirginligi-14944934-haberi/ ;
  //   https://www.gazetebirlik.com/magazin/umit-yasar-maslak-mahsusta-konser-verdi/1108001
  // - Practice: know the makam and the pitches first; short groups of notes; one vowel; a light voice; slow and
  //   then faster with a metronome; staccato to fix smeared notes; phrases that fit one breath; no tension in neck,
  //   tongue, lips and jaw; imitate a master: https://www.vocology.utah.edu/_resources/documents/slow_practice_maxfield.pdf ;
  //   https://repositorio.ufmg.br//bitstreams/2377e6e5-8656-4819-941c-160de6cc3836/download (Pereira 2021) ;
  //   https://aura.ujep.cz/pdfs/aum/2012/02/07.pdf (Zelenková 2012) ; https://idildergisi.com/makale/pdf/1627386581.pdf ;
  //   https://www.idildergisi.com/makale/pdf/1625623259.pdf ; https://www.30daysinger.com/tutorial/vocal-agility-for-high-voices/6 ;
  //   https://islamansiklopedisi.org.tr/gazel--musiki
  {
    id: 'nagme',
    topic: 'vokal',
    title: 'Nağmeli söyleyiş nasıl gelişir?',
    teaser: `Bir heceyi birçok notaya yaymak doğuştan gelen bir sır değil; hafif bir sesle, yavaştan hızlıya çalışılan bir beceri.`,
    minutes: 5,
    blocks: [
      tip('info', 'Ezberlenen', `"Nağme ya vardır ya yoktur."`),
      p(
        `Tek bir heceyi birden çok notaya yayarak söylemeye **melizma** denir; her heceye tek nota düşen söyleyişin tersidir. Nağme kelimesi ise Türkçede ezgi, melodi demektir.`,
      ),
      p(
        `Türk halk müziğinde şarkıcının süsleme becerisine **hançere** denir. "Gırtlak yapmak", "gırtlak atmak" ya da "gırtlak nağmesi" gibi sözler bunu anlatır: ses telleriyle yapılan tril, mordan, çarpma, grupetto ve vibrato gibi süslemeler. Hançere yumuşak, orta sert ya da sert olabilir.`,
      ),
      p(
        `Süsleme en çok serbest ritimli (usulsüz) **uzun havalarda** duyulur, ama hızlı oyun havalarında da hızlı gırtlak nağmeleri vardır. Arabeskte de hem şarkıcının hem çalgıların icrası zengin süslemelerle doludur. Müslüm Gürses, Ferdi Tayfur ve Ümit Yaşar bu türün tanınan sesleri arasındadır.`,
      ),
      play('Beş notalık kalıp, yavaş', {
        glide: 'A3:0.5 Bb3:0.5 C#4:0.5 D4:0.5 E4 D4:0.5 C#4:0.5 Bb3:0.5 A3:3~',
        bpm: 60,
      }),
      play('Aynı kalıp, hızlı', { glide: 'A3:0.5 Bb3:0.5 C#4:0.5 D4:0.5 E4 D4:0.5 C#4:0.5 Bb3:0.5 A3:3~', bpm: 132 }),
      {
        t: 'keyboard',
        from: 'A3',
        to: 'A4',
        highlight: 'A3 A#3 C#4 D4 E4',
        caption: `Kalıbın notaları: {{pc:A}} üzerindeki Hicaz dörtlüsünün ({{pc:A}}–{{pc:Bb}}–{{pc:C#}}–{{pc:D}}) gitar ve piyanodaki yaklaşık sesleri ve {{pc:E}}. Kalın sesler bir oktav aşağıda, ince sesler bir oktav yukarıda söyler.`,
      },
      h(`Neden zor?`),
      p(
        `Nağmede perde çok kısa sürede değişir ve her nota yerine tam oturmalıdır. Boyundaki, dildeki, dudaklardaki ve çenedeki gerginlik bu çevikliği bozar. Bu yüzden kaynakların çoğu süslemeyi önce hafif bir sesle ve rahat bir gırtlakla çalışmayı önerir.`,
      ),
      h(`Nasıl çalışılır?`),
      steps([
        [
          'Makamı ve notaları öğren',
          `Süslemeye geçmeden önce ezginin notalarını ve makamını tanı. Notaları yavaşça, tek tek söyle.`,
        ],
        [
          'Kısa kalıplarla başla',
          `İki, üç, sonra beş notalık kalıplar söyle. Uzun bir nağmeyi dörder notalık gruplara böl.`,
        ],
        [
          'Tek ünlü, hafif ses',
          `Önce tek bir ünlüyle ("a") ve az sesle söyle. Rahatladıkça "e", "i" ve "ı" ile de dene.`,
        ],
        ['Yavaştan hızlıya', `Metronomla her notanın temiz çıktığı bir tempoda başla, sonra tempoyu adım adım artır.`],
        [
          'Bulanıklaşırsa ayır',
          `Notalar birbirine karışıp kaymaya dönüşürse kalıbı kesik kesik (staccato) söyle, sonra yeniden bağla.`,
        ],
        ['Tek nefese sığdır', `Yavaş tempoda tek nefeste söyleyebildiğin bir cümle seç.`],
        [
          'Ustayı taklit et',
          `Bir ustanın kaydından kısa bir bölümü tekrar tekrar dinleyip taklit et. Halk müziğinde bu, meşk geleneğinin parçasıdır.`,
        ],
      ]),
      compare([
        ['İki nota', { glide: 'A3:0.5 Bb3:0.5 A3:2', bpm: 70 }],
        ['Üç nota', { glide: 'A3:0.5 Bb3:0.5 C#4:0.5 Bb3:0.5 A3:2', bpm: 70 }],
        ['Kesik kesik', pn({ seq: 'A3:0.5 Bb3:0.5 C#4:0.5 Bb3:0.5 A3:2', bpm: 70 })],
      ]),
      go(`Gam Merdiveni: 5'li gam`, 'vokal.egz.gam.1'),
      go('Melodi Yankısı', 'vokal.egz.yanki.1'),
      go('Canlı ses analizini aç', 'araclar.analiz'),
    ],
  },
  // Sources:
  // - Acciaccatura: a short note crushed in before the main note, the stress on the main note:
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/acciaccatura (Concise Oxford
  //   Dictionary of Music) ; https://en.wikipedia.org/wiki/Ornament_(music)
  // - Turkish "çarpma" = acciaccatura: https://dergipark.org.tr/tr/pub/gefad/article/90999 (Özçelik 2002) ;
  //   https://tymm.meb.gov.tr/muzik-okullari-bireysel-calgi-egitimi-dersi-keman-modulu/unite/1322
  // - Appoggiatura: on the beat, displaces the main note and resolves to it by step, usually half its value:
  //   https://www.britannica.com/art/appoggiatura ;
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/appoggiatura
  // - Mordent: main-upper-main or main-lower-main; "inverted mordent" means opposite things in different books, so
  //   say upper/lower: https://en.wikipedia.org/wiki/Mordent ;
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/mordent ;
  //   https://dergipark.org.tr/tr/pub/gefad/article/90999
  // - Trill: rapid alternation with the note a semitone or tone above; Baroque trills start on the upper note,
  //   later ones on the main note: https://en.wikipedia.org/wiki/Trill_(music) ;
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/trill ;
  //   https://dergipark.org.tr/tr/pub/gefad/article/90999
  // - On guitar a trill is a series of hammer-ons and pull-offs: https://en.wikipedia.org/wiki/Trill_(music) ;
  //   https://www.fender.com/articles/techniques/master-hammer-ons-and-pull-offs
  // - Turn (grupetto): note above, the note, note below, the note: https://en.wikipedia.org/wiki/Ornament_(music) ;
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/gruppetto ;
  //   https://tymm.meb.gov.tr/muzik-okullari-bireysel-calgi-egitimi-dersi-keman-modulu/unite/1322
  // - Glissando and portamento overlap: stepped on piano and harp, continuous on voice and strings:
  //   https://en.wikipedia.org/wiki/Glissando ;
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/glissando ;
  //   https://www.britannica.com/art/portamento
  // - Gırtlak nağmesi is built from these ornaments: https://www.idildergisi.com/makale/pdf/1625623259.pdf ;
  //   https://dergipark.org.tr/tr/download/article-file/4571299
  {
    id: 'susleme',
    topic: 'vokal',
    title: 'Çarpma, mordan, tril, grupetto: süslemeleri kulakla tanı',
    teaser: `Süslü söyleyişin yapı taşları birkaç küçük kalıptır. Adlarını ve seslerini bilince şarkıcıların ne yaptığını duymaya başlarsın.`,
    minutes: 4,
    blocks: [
      tip('info', 'Ezberlenen', `"Şarkıcı sesini kıvırıyor."`),
      p(
        `Süslemeler, ana notanın çevresine eklenen kısa notalardır. Melodiyi değiştirmezler, ona renk katarlar. Halk müziğinde gırtlak nağmesi denen süslemeler de büyük ölçüde bunlardan oluşur. Aşağıdaki örneklerin hepsinde ana nota {{n:A3}}.`,
      ),
      table(
        ['Süsleme', 'Ne yapar?', 'Dinle'],
        [
          [
            'Çarpma',
            `Ana notadan hemen önce, çarpıp kaçar gibi çok kısa söylenen süs notası. Vurgu ana notadadır.`,
            cell({ glide: 'B3:0.12 A3:3', bpm: 60 }),
          ],
          [
            'Appoggiatura',
            `Vuruşun üstüne oturur, ana notanın yerini bir süre alır ve ona adım adım çözülür; çoğunlukla ana notanın yarı süresini alır.`,
            cell({ glide: 'B3:1.5 A3:1.5', bpm: 60 }),
          ],
          [
            'Üst mordan',
            `Ana nota, bir üst komşusu ve yeniden ana nota, hızlıca.`,
            cell({ glide: 'A3:0.12 B3:0.12 A3:3', bpm: 60 }),
          ],
          [
            'Alt mordan',
            `Ana nota, bir alt komşusu ve yeniden ana nota.`,
            cell({ glide: 'A3:0.12 G#3:0.12 A3:3', bpm: 60 }),
          ],
          [
            'Tril',
            `Ana nota ile bir üstündeki nota arasında hızlı gidip gelme. Aradaki mesafe yarım ya da tam sestir.`,
            cell({ glide: TRILL, bpm: 60 }),
          ],
          [
            'Grupetto',
            `Üst komşu, ana nota, alt komşu ve yeniden ana nota.`,
            cell({ glide: 'B3:0.2 A3:0.2 G#3:0.2 A3:3', bpm: 60 }),
          ],
          ['Glissando, portamento', `Bir notadan ötekine kayarak geçiş.`, cell({ glide: 'E3 /A3:2 A3:2', bpm: 60 })],
        ],
      ),
      h(`Az bilinen ayrıntılar`),
      list([
        `**Barok dönemde tril üst notadan başlar.** Mozart'tan sonra tril çoğunlukla ana notadan başlar.`,
        `**"Ters mordan" kafa karıştırır.** Kimi kaynak bu adı alt mordana, kimi üst mordana verir. Karışıklığı önlemek için "üst mordan" ve "alt mordan" demek önerilir.`,
        `**Glissando mu, portamento mu?** Piyano ve arpta glissando tek tek basamaklardan geçer; seste ve kemanda kayma kesintisizdir. Kesintisiz kaymaya çoğu zaman portamento denir, ama iki terim sık sık birbirinin yerine kullanılır.`,
        `**Gitarda tril**, teli yeniden çalmadan perdeye vurup (hammer-on) parmağı çekerek (pull-off) yapılır.`,
      ]),
      compare([
        ['Barok tril: üst notadan', { glide: `B3:0.125 ${TRILL}`, bpm: 60 }],
        ['Sonraki dönem: ana notadan', { glide: TRILL, bpm: 60 }],
        [
          'Piyanoda glissando: basamak basamak',
          pn({ seq: 'E3:0.25 F3:0.25 F#3:0.25 G3:0.25 G#3:0.25 A3:2', bpm: 120 }),
        ],
        ['Seste portamento: kesintisiz', { glide: 'E3 /A3:1 A3:2', bpm: 120 }],
      ]),
      go('Melodi Yankısı', 'vokal.egz.yanki.1'),
      go('Siren Takibi: Yavaş', 'vokal.egz.siren.1'),
    ],
  },
  // Sources:
  // - Vocal vibrato rate about 5-7 Hz (5-8 Hz; means near 6 Hz): https://www.speech.kth.se/qpsr/1994/1994_35_2-3_045-068.pdf
  //   (Sundberg) ; https://ncvs.org/tutorials/fluctuations-and-perturbations-of-the-voice/ ;
  //   https://www.speech.kth.se/qpsr/1992/1992_33_4_073-086.pdf (Prame) ; https://pmc.ncbi.nlm.nih.gov/articles/PMC8995401/
  // - Extent usually under a semitone either way, often about +-50 cents: Sundberg (above) ;
  //   https://ncvs.org/tutorials/fluctuations-and-perturbations-of-the-voice/ ;
  //   https://www.speech.kth.se/qpsr/2002/2002_43_1_037-044.pdf (Bretos & Sundberg)
  // - Heard as one pitch at its mean: Sundberg (above) ; https://pubmed.ncbi.nlm.nih.gov/8817899/ (Brown & Vaughn 1996)
  // - Slower than about 5 Hz sounds too slow (a wobble), faster than 8 Hz nervous: Sundberg (above) ;
  //   Prame (above, citing Seashore) ; https://www.sci.brooklyn.cuny.edu/~jones/Shirlee/vibrato.html
  // - Vibrato varies pitch; tremolo varies loudness or repeats a note quickly; a trill alternates two notes:
  //   https://www.encyclopedia.com/arts/dictionaries-thesauruses-pictures-and-press-releases/tremolo ;
  //   https://www.britannica.com/art/vibrato ;
  //   https://www.fender.com/articles/techniques/pitch-or-volume-the-difference-between-tremolo-and-vibrato ;
  //   https://en.wikipedia.org/wiki/Tremolo
  // - Bend vibrato only raises the pitch; classical (axial) vibrato and a floating vibrato arm go both ways:
  //   https://en.wikipedia.org/wiki/Finger_vibrato ; https://douglasniedt.com/Tech_Tip_Vibrato_Part_1.html ;
  //   https://www.premierguitar.com/lessons/shake-it-off-everything-you-need-to-know-about-vibrato ;
  //   https://en.wikipedia.org/wiki/Vibrato_systems_for_guitar ; https://www.premierguitar.com/floating-tremolos
  // - Fender called its vibrato arm "tremolo" and its tremolo amp circuit "vibrato":
  //   https://www.fender.com/articles/techniques/pitch-or-volume-the-difference-between-tremolo-and-vibrato ;
  //   https://en.wikipedia.org/wiki/Vibrato_systems_for_guitar
  {
    id: 'vibrato-tril',
    topic: 'vokal',
    title: 'Vibrato, tremolo ve tril aynı şey değil',
    teaser: `Vibrato bir notanın perdesini dalgalandırır, tremolo sesin gücünü ya da aynı notayı tekrarlar, tril iki ayrı nota arasında gidip gelir.`,
    minutes: 4,
    blocks: [
      tip('info', 'Ezberlenen', `"Uzun notada sesi titret."`),
      p(
        `**Vibrato**, tek bir notanın perdesinin düzenli olarak biraz yukarı ve biraz aşağı salınmasıdır. Eğitimli şarkıcılarda saniyede yaklaşık 5–7 kez salınır; açıklığı çoğunlukla yarım sesten azdır, sık sık ±50 sent civarındadır. Kulak bu salınımı ayrı notalar olarak değil, ortalamasındaki tek bir nota olarak duyar.`,
      ),
      play('Doğal vibrato', { glide: 'A3:4~5.5/50', bpm: 60 }),
      table(
        ['Ne?', 'Nasıl duyulur?', 'Dinle'],
        [
          ['Vibrato', `Tek nota; perde saniyede 5–7 kez dalgalanır.`, cell({ glide: 'A3:4~6/50', bpm: 60 })],
          [
            'Çok yavaş vibrato',
            `Saniyede 5'ten yavaş: sallanan, kararsız bir ses.`,
            cell({ glide: 'A3:4~3.5/100', bpm: 60 }),
          ],
          [
            'Çok hızlı vibrato',
            `Saniyede 8'den hızlı: gergin, titrek bir ses.`,
            cell({ glide: 'A3:4~8.5/30', bpm: 60 }),
          ],
          [
            'Tremolo',
            `Perde değil, ses gücü dalgalanır ya da aynı nota hızla tekrarlanır.`,
            cell({ seq: `${'A3:0.25 '.repeat(15)}A3`, bpm: 120 }),
          ],
          ['Tril', `İki ayrı nota arasında hızlı gidip gelme.`, cell({ glide: TRILL, bpm: 60 })],
        ],
      ),
      h(`Gitarda vibrato hangi yöne?`),
      p(
        `Elektro gitarda vibrato çoğunlukla teli perdeye basılıyken yana itip bırakarak yapılır. Teli itmek gerginliği artırır ve sesi inceltir; bırakınca ses basılan notaya döner. Bu yüzden bu vibrato yalnızca notanın **üstünde** salınır. Klasik gitarda parmak telin boyunca ileri geri oynatılır ve ses iki yöne de salınır; serbestçe hareket eden bir vibrato kolu da sesi hem inceltir hem kalınlaştırır.`,
      ),
      compare([
        ['Seste: notanın iki yanında', { glide: 'A3:4~5/40', bpm: 60 }],
        ['Gitarda bükerek: yalnız yukarıda', { glide: 'A3+40c:4~5/40', bpm: 60 }],
      ]),
      tip(
        'info',
        'Fender adları ters koydu',
        `Fender, gitarlarında perdeyi oynatan kola "tremolo", amfilerinde ses gücünü dalgalandıran devreye ise "vibrato" adını verdi. İkisi de ters; gitarcıların "tremolo kolu" dediği şey aslında vibrato yapar.`,
      ),
      go('Vibrato dersi', 'vokal.ders.v9'),
      go('Canlı ses analizini aç', 'araclar.analiz'),
    ],
  },
  // Sources:
  // - Breathing out is relaxation of the diaphragm and elastic recoil; the diaphragm is the main muscle for breathing
  //   in: https://en.wikipedia.org/wiki/Thoracic_diaphragm ; https://my.clevelandclinic.org/health/body/21578-diaphragm ;
  //   https://www.speech.kth.se/qpsr/1992/1992_33_1_049-064.pdf (Sundberg, "Breathing behavior during singing")
  // - Singing needs a steady pressure below the folds: inspiratory muscles brake it at high lung volumes, the
  //   abdominal muscles take over as the lungs empty; support is an interplay of both: Sundberg (above) ;
  //   http://sites.music.mcgill.ca/mpbs/out-of-breath/breathing-myths-debunked ; https://pmc.ncbi.nlm.nih.gov/articles/PMC4861272
  // - "Sing from your diaphragm" is misleading: http://sites.music.mcgill.ca/mpbs/out-of-breath/breathing-myths-debunked ;
  //   Sundberg (above: "the diaphragm is an inhalatory muscle")
  // - No agreed definition of support; some singers co-contract the diaphragm through the phrase: Sundberg (above) ;
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC4861272 ; http://sites.music.mcgill.ca/mpbs/out-of-breath/breathing-myths-debunked
  {
    id: 'diyafram',
    topic: 'vokal',
    title: `"Diyaframdan söyle" ne demek?`,
    teaser: `Diyafram bir nefes alma kasıdır. Şarkı söylerken havayı asıl karın kasları verir; "destek" bu ikisinin dengesidir.`,
    minutes: 4,
    blocks: [
      tip('info', 'Ezberlenen', `"Diyaframdan söyle."`),
      p(
        `Diyafram, göğüs boşluğunu karın boşluğundan ayıran kubbe biçimli bir kastır ve nefes almanın ana kasıdır. Nefes alırken kasılıp düzleşir, akciğerlere yer açar. Sakin nefes verirken gevşer ve yeniden kubbeleşir; havayı akciğerlerin esnekliği dışarı iter.`,
      ),
      p(
        `Şarkı söylemek nefes verirken olur ve ses tellerinin altında sabit bir hava basıncı ister. Akciğerler doluyken bu basınç kendiliğinden fazladır; nefes alma kasları bir fren gibi çalışıp onu azaltır. Akciğerler boşaldıkça işi giderek nefes verme kasları, özellikle karın kasları devralır.`,
      ),
      table(
        ['Kas', 'Görevi', 'Şarkı söylerken'],
        [
          ['Diyafram', 'Nefes alma kası', 'Akciğerler doluyken fren olabilir; sonra gevşer.'],
          ['Karın kasları', 'Nefes verme kası', 'Akciğerler boşaldıkça havayı içeriden iter.'],
        ],
      ),
      h(`Peki neden "diyafram" denir?`),
      p(
        `İyi bir şarkı nefesi, diyaframın aşağı indiği derin bir nefesle başlar ve bu genişliği bir anda bırakmamayı ister. "Diyaframdan söyle" sözü bunun kısa ama yanıltıcı bir adıdır: sesi diyafram vermez, diyafram nefesi hazırlar.`,
      ),
      tip(
        'info',
        `"Destek"in tek bir tanımı yok`,
        `Şan eğitiminde "nefes desteği" çok kullanılır, ama herkesin kabul ettiği tek bir tanımı yoktur. Araştırmalar şarkıcıların farklı yollar izlediğini gösterir; bazıları diyaframı cümle boyunca da kasılı tutar.`,
      ),
      { t: 'breath', pattern: [4, 4, 8], rounds: 2 },
      steps([
        ['Derin al', `Omuzlarını kaldırmadan, belinin çevresi genişleyecek şekilde nefes al.`],
        ['Genişliği koru', `Nefes verirken kaburgalarını ve belini bir anda bırakma.`],
        ['Eşit ver', `Aşağıdaki notayı "aa" ile, ses ne patlayarak başlayıp ne sönerek bitecek şekilde eşit tut.`],
      ]),
      play('Tutmak için nota', { glide: 'A3:8', bpm: 60 }),
      go('Nefes dersi', 'vokal.ders.v2'),
      go('"Sss" süresini ölç', 'vokal.egz.nefes.2'),
    ],
  },
  // Sources:
  // - Semi-occlusion (straw, lip or tongue trill, humming, voiced fricatives) raises the pressure in the mouth and
  //   above the folds; phonation threshold pressure drops, fold amplitude and collision force fall:
  //   https://iro.uiowa.edu/esploro/outputs/journalArticle/Voice-training-and-therapy-with-a/9984719747602771 (Titze 2006) ;
  //   https://vocology.utah.edu/_resources/documents/major_benefits_of_sovtes_titze.pdf (Titze 2018) ;
  //   https://www.nats.org/_Library/JOS_On_Point/JOS_077_03_2021_333.pdf (Hijleh & Pinto 2021) ;
  //   https://www.bjorl.org/en-immediate-effects-phonation-into-straw-articulo-S1808869415306790 (Costa 2011)
  // - A narrower straw resists more; diameter matters far more than length or material:
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC5085896/ (Smith & Titze) ; Titze 2018 (above)
  // - Humming is the mildest, a straw in water the strongest: https://pmc.ncbi.nlm.nih.gov/articles/PMC4970513/
  //   (Maxfield 2014) ; Hijleh & Pinto (above)
  // - Lip trills, nasals and other semi-occlusions belong to the same family:
  //   https://www.vocology.utah.edu/_resources/documents/intro_a_video_straw_phonation_titze.pdf ; Hijleh & Pinto (above)
  // - Straw exercise: thin straw, lips sealed (pinching the nose changes nothing), glides low to high, a song through
  //   the straw, 2-5 minutes several times a day; a wider straw if it feels pressed; see a voice therapist if
  //   hoarseness stays: https://www.vocology.utah.edu/_resources/documents/intro_a_video_straw_phonation_titze.pdf ;
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC4610291/ (Kapsner-Smith 2015) ; Titze 2018 (above)
  {
    id: 'pipet',
    topic: 'vokal',
    title: 'Pipetle şarkı söylemek neden işe yarar?',
    teaser: `Ağız daralınca ses tellerinin hemen üstünde hafif bir basınç birikir. Bu basınç ses tellerinin daha az çabayla, daha yumuşak çarpışarak titreşmesini sağlar.`,
    minutes: 4,
    blocks: [
      tip('info', 'Ezberlenen', `Isınmaya dudak titreşimiyle ("brrr") ya da mırıldanarak başla.`),
      p(
        `Dudak titreşimi, mırıldanma, "vvv" sesi ve pipetle söylemenin ortak noktası, ses yolunun önünün kısmen kapalı olmasıdır. Bunlara **yarı kapalı ses yolu egzersizleri** denir.`,
      ),
      p(
        `Ağız daraldığında ağızdaki ve ses tellerinin hemen üstündeki hava basıncı yükselir. Bu geri basınç sayesinde ses telleri daha az hava basıncıyla titreşmeye başlar, daha dar salınır ve birbirine daha yumuşak çarpar. Sonuç: aynı sesi daha az zorlanarak çıkarırsın.`,
      ),
      table(
        ['Egzersiz', 'Nasıl?', 'Kılavuzla kay'],
        [
          [
            'Mırıldanma ("mmm")',
            `Dudaklar kapalı, ses burundan çıkar. Ailenin en hafif üyesidir.`,
            cell({ glide: 'C4:2 /G4:2 G4 /C4:2', bpm: 80 }),
          ],
          [
            'Dudak titreşimi ("brrr")',
            `Dudaklar gevşek; aralarından geçen hava onları titreştirir.`,
            cell({ glide: 'C4 /G4:2 /C4:2', bpm: 80 }),
          ],
          [
            'Pipetle söyleme',
            `Dudaklar pipeti sarar, hava yalnızca pipetten çıkar. Pipet inceldikçe direnç artar.`,
            cell({ glide: 'C4 /C5:3 /C4:3', bpm: 80 }),
          ],
          [
            'Suya daldırılmış pipet',
            `En güçlüsü: pipetin ucu bir bardak suyun içinde, söylerken kabarcıklar çıkar.`,
            cell({ glide: 'C4:2 /G4:2 /C4:2', bpm: 80 }),
          ],
        ],
      ),
      steps([
        [
          'Pipeti seç',
          `Normal bir içecek pipetiyle başla; alıştıkça daha ince bir pipete geç. Ses sıkışıyorsa yeniden kalın pipete dön.`,
        ],
        [
          'Dudaklarını kapat',
          `Pipetin ucunu dudaklarınla sar. Burnunu sıktığında ses değişmemeli; hava yalnızca pipetten çıkmalı.`,
        ],
        ['Kay', `En kalın sesinden en ince sesine kadar yavaşça kay, sonra geri in. Birkaç kez tekrarla.`],
        ['Şarkını söyle', `Bildiğin bir ezgiyi baştan sona pipetten söyle.`],
        ['Kısa tut', `Günde birkaç kez 2–5 dakika yeter.`],
      ]),
      tip(
        'warn',
        'Ses kısıklığı geçmiyorsa',
        `Bu egzersizler bir tedavi değildir. Ses kısıklığın geçmiyorsa bir ses terapistine ya da KBB uzmanına görün.`,
      ),
      go('Isınma rutini', 'vokal.isinma'),
      go('Siren Takibi: Yavaş', 'vokal.egz.siren.1'),
    ],
  },
  // Sources:
  // - The folds open and close at the frequency of the note: 440 times a second for A4; about 220 for a woman's and 100
  //   for a man's speaking voice: https://laryngopedia.com/?p=349 ;
  //   https://www.wsh.nhs.uk/CMS-Documents/Patient-leaflets/SpeechandLanguageTherapyServices/6534-1-Practical-voice-care.pdf
  // - "Vocal folds" is preferred to "vocal cords": layered mucosa over the vocalis muscle, not strings:
  //   https://en.wikipedia.org/wiki/Vocal_folds ; NHS leaflet (above)
  // - Speaking pitch: men about 100-125 Hz, women about 200-220 Hz, children above 300 Hz: https://en.wikipedia.org/wiki/Vocal_folds ;
  //   https://www.sydney.edu.au/content/dam/corporate/documents/faculty-of-medicine-and-health/research/research-centres/sydney-voice-lab/acoustic-analysis-normative-data-reference-sheet-20250416_v4.0-sydney-voice-lab.pdf.coredownload.pdf ;
  //   https://en.wikipedia.org/wiki/Voice_frequency ; NHS leaflet (above)
  // - Pitch rises as muscles (cricothyroid) lengthen and tense the folds; tension is the main control:
  //   https://en.wikipedia.org/wiki/Vocal_folds ; https://ncvs.org/?p=8658
  // - Swallowed drinks never touch the folds (the folds close, the epiglottis sends them down the oesophagus); hydration
  //   reaches them through the body, how fast is unknown; steam reaches their surface: https://utswmed.org/medblog/vocal-cords-care-qa/ ;
  //   NHS leaflet (above) ; https://en.wikipedia.org/wiki/Swallowing ; https://en.wikipedia.org/wiki/Epiglottis ;
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC2925668/ ;
  //   https://voicefoundation.org/wp-content/uploads/2013/11/2013_FallVol18_Issue_2_Newsletter.pdf ;
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC6283588/
  // - 5 hours at 220 Hz = 3.96 million cycles is computed.
  {
    id: 'ses-telleri',
    topic: 'vokal',
    title: `{{n:A4}} söylerken ses tellerin saniyede 440 kez açılıp kapanır`,
    teaser: `Duyduğun notanın frekansı, ses tellerinin saniyedeki titreşim sayısıdır. Bol konuşulan bir günde bu sayı milyonları bulur.`,
    minutes: 3,
    blocks: [
      tip('info', 'Ezberlenen', `Ses telleri gitar telleri gibi titreşir.`),
      p(
        `Ses telleri aslında gergin teller değildir: gırtlakta karşılıklı duran, üstü yumuşak bir zarla kaplı iki kas kıvrımıdır. Bu yüzden bilim insanları İngilizcede "ses telleri" (vocal cords) yerine "ses kıvrımları" (vocal folds) demeyi tercih eder.`,
      ),
      p(
        `Şarkı söylerken ses tellerin saniyede yüzlerce kez açılıp kapanır. Saniyedeki bu açılıp kapanma sayısı, duyduğun notanın frekansıdır: {{n:A4}} söylerken 440, bir oktav aşağıdaki {{n:A3}} notasında 220 kez.`,
      ),
      compare([
        ['{{n:A3}}: saniyede 220 kez', pn({ seq: 'A3:3' })],
        ['{{n:A4}}: saniyede 440 kez', pn({ seq: 'A4:3' })],
      ]),
      table(
        ['Konuşurken', 'Ortalama frekans', 'Yakın nota', 'Dinle'],
        [
          ['Yetişkin erkek', 'yaklaşık 100–125 Hz', '{{n:G2}} – {{n:B2}}', cell(pn({ seq: 'A2:2' }))],
          ['Yetişkin kadın', 'yaklaşık 200–220 Hz', '{{n:G3}} – {{n:A3}}', cell(pn({ seq: 'G#3:2' }))],
          ['Çocuk', `300 Hz'in üstü`, '{{n:D#4}} ve üstü', cell(pn({ seq: 'D#4:2' }))],
        ],
      ),
      p(
        `Günde 5 saat konuşan ve sesi ortalama 220 Hz olan biri için bu, günde yaklaşık 4 milyon açılıp kapanma demektir. Notayı inceltmek için gırtlaktaki küçük kaslar ses tellerini uzatıp gerer; notanın yüksekliğini belirleyen başlıca etken bu gerginliktir.`,
      ),
      tip(
        'info',
        'İçtiğin su ses tellerine değmez',
        `Yutkunurken ses tellerin kapanır ve epiglot denen kapak gırtlağın üstüne kapanır; su, çay ya da bal yemek borusuna gider. İçtiğin su ses tellerine vücudun üzerinden ulaşır ve bunun ne kadar sürdüğü henüz bilinmiyor. Buhar ise nefesle gırtlaktan geçer ve ses tellerinin yüzeyini kısa bir süre nemlendirir.`,
      ),
      go('Oktavlar ve frekans dersi', 'nota.ders.n5'),
      go('Notayı Tut: Kolay', 'vokal.egz.tut.1'),
    ],
  },
  // Sources:
  // - You hear your own voice through the air and through the skull (bone conduction); a recording keeps only the air
  //   part: https://en.wikipedia.org/wiki/Bone_conduction ; https://scientificamerican.com/article/why-does-my-voice-sound-different ;
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC9929504
  // - The skull path favours low frequencies, so your voice sounds lower and fuller to you:
  //   https://en.wikipedia.org/wiki/Bone_conduction ; https://scientificamerican.com/article/why-does-my-voice-sound-different ;
  //   https://pmc.ncbi.nlm.nih.gov/articles/PMC9929504 ; https://tr.wikipedia.org/wiki/Kemik_iletim
  {
    id: 'kayitta-sesin',
    topic: 'vokal',
    title: 'Kayıtta sesin neden yabancı gelir?',
    teaser: `Konuşurken kendi sesini iki yoldan duyarsın: havadan ve kafatasının içinden. Kayıt yalnızca birincisini yakalar.`,
    minutes: 3,
    blocks: [
      tip('info', 'Ezberlenen', `"Kayıtta sesim hiç böyle değil, mikrofon bozuyor."`),
      p(
        `Başkaları sesini yalnızca havadan gelen titreşimlerle duyar. Sen ise konuşurken ve şarkı söylerken sesini iki yoldan alırsın: ağzından çıkıp kulağına havadan gelen ses ve titreşimin kafatası kemiklerinden doğrudan iç kulağa ulaşan kısmı. Buna **kemik iletimi** denir.`,
      ),
      table(
        ['Yol', 'Kim duyar?', 'Etkisi'],
        [
          ['Hava yolu', 'Herkes, mikrofon da', 'Sesin başkalarına ulaşan hâli'],
          ['Kemik yolu', 'Yalnızca sen', 'Sesini sana daha kalın ve dolgun duyurur'],
        ],
      ),
      p(
        `Kafatası kalın frekansları havadan daha iyi iletir. Bu yüzden kendi sesini olduğundan daha kalın ve dolgun duyarsın. Kayıtta kemik yolu yoktur; sesin sana ince ve yabancı gelir. Başkalarının duyduğu ise kayıttakine çok daha yakındır.`,
      ),
      h(`Şarkı söylerken neden önemli?`),
      p(
        `Kendi kulağın sesini olduğundan farklı duyduğu için, söylerken perde ve renk hatalarını fark etmek zordur. Kendini kaydedip dinlemek, sesini başkalarının duyduğuna yakın duymanın en kolay yoludur.`,
      ),
      steps([
        ['Kalıbı dinle', `Aşağıdaki kalıbı bir kez dinle.`],
        ['Söyle ve kaydet', `Telefonunun ses kaydedicisini aç ve kalıbı "aa" ünlüsüyle söyle.`],
        ['Karşılaştır', `Kaydı dinle ve referansla karşılaştır. Hangi notada kaydığını not et.`],
      ]),
      play(`Kaydetmek için kalıp`, pn({ seq: 'C4 E4 G4 E4 C4:2', bpm: 80 })),
      tip(
        'success',
        'Ekranda gör',
        `Notayı Tut egzersizinde mikrofon, söylediğin notayı anında gösterir. Kulağının kaçırdığını ekran yakalar.`,
      ),
      go('Notayı Tut: Kolay', 'vokal.egz.tut.1'),
      go('Perde doğruluğu dersi', 'vokal.ders.v5'),
    ],
  },
];
